import React, { useState, useEffect, useContext } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoice, faCheckCircle, faUserShield, faSave, faPaperPlane, faFilePdf, faColumns } from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';

import CreditNoteScreen1 from './CreditNoteScreen1';
import CreditNoteScreen2 from './CreditNoteScreen2';
import CreditNoteScreen3 from './CreditNoteScreen3';
import invoicingService from '../../services/invoicingService';
import { extractApiError } from '../../utils/errorMessages';
import { useFlashMessage } from '../../components/FlashMessage';
import { AuthContext } from '../../context/AuthContext';
import FinancialApprovalModal from '../../components/Invoicing/FinancialApprovalModal';
import InvoiceValidationModal from '../../components/Invoicing/InvoiceValidationModal';
import DeliveryLinkBadge from '../../components/Invoicing/DeliveryLinkBadge';
import AbacusStatus from '../../components/Abacus/AbacusStatus';
import HMSPageHeader from '../../components/ui/HMSPageHeader';
import HMSStepper from '../../components/ui/HMSStepper';
import DocumentsSplitPane from '../../components/DocumentsSplitPane';
import RelatedDocumentsPane, { useInvoicePapersOpen } from '../../components/RelatedDocumentsPane';
import useDocumentReturn from '../../components/shared/useDocumentReturn';
import PdfPreviewModal from '../../components/shared/PdfPreviewModal';
import RecordLoadProblem, { describeLoadFailure } from '../../components/ui/RecordLoadProblem';
import './OutgoingInvoice.css';
import PinToDeskButton from '../../components/mydesk/PinToDeskButton';
import { pledgeOfDocument } from '../../components/shared/PledgeBankField';

/**
 * Create/Edit Credit Note - 3-Screen Flow Orchestrator
 * 
 * Manages:
 * - State for all 3 screens
 * - Navigation between screens
 * - Save draft functionality
 * - Submit for workflow
 * - Creation from outgoing invoice
 * - Editing existing credit notes
 */

const CreateCreditNote = () => {
  const history = useHistory();
  const { showSuccess, showError } = useFlashMessage();
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const { id } = useParams();
  
  // Check if creating from invoice
  const sourceInvoice = location.state?.invoice || null;
  
  // State management
  const [currentScreen, setCurrentScreen] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(!!id);
  // Row 125: why the credit note in the address didn't load. While set, the
  // screen shows RecordLoadProblem INSTEAD of the editor, and the id that did
  // load is what lets a save through -- a blank editor must never be saved
  // over the real document.
  const [loadProblem, setLoadProblem] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [loadedId, setLoadedId] = useState(null);
  // The id of the document this screen is editing -- from the URL when we
  // arrived on one, or adopted from the server the moment a draft is first
  // created. Held here so a second Save Draft UPDATES the document instead of
  // POSTing a duplicate.
  //
  // isEditMode used to be useState(!!id), and setIsEditMode was never called
  // anywhere in five of the six creators, so the flag was frozen at mount. The
  // old redirect-to-the-list hid that: you left the screen before you could
  // press Save Draft twice. Now that saving keeps you here, the flag has to
  // follow the document.
  const [createdId, setCreatedId] = useState(null);
  const documentId = id || createdId;
  // PDF preview - available on any SAVED note, read-only view included, so
  // finance can check the document's formatting before approving it.
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const isEditMode = Boolean(documentId);
  // Invoice Papers: the note's PDF, the invoice it corrects and the delivery's papers and contracts BESIDE the note, so an approver checks one
  // against the other without leaving the screen (ClickUp 2026-10-23,
  // "double screen ... where there are approvals"). Remembered per browser,
  // shared with the invoice screens.
  const invoicePapers = useInvoicePapersOpen();
  // Who RAISED this document. Dropped by this loader until 2026-08-26, which
  // left Screen 1 showing whoever was logged in instead.
  const [documentIssuerName, setDocumentIssuerName] = useState(null);
  const [creditNoteStatus, setCreditNoteStatus] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  
  // Determine if credit note is locked (read-only) based on status
  const isReadOnly = creditNoteStatus && !['DRAFT', 'PENDING_APPROVAL', 'REJECTED'].includes(creditNoteStatus);
  
  // Validation modal state
  const [showDraftValidationModal, setShowDraftValidationModal] = useState(false);
  const [draftValidationErrors, setDraftValidationErrors] = useState([]);
  const [targetScreenForValidation, setTargetScreenForValidation] = useState(null);
  
  // Credit Note data (header)
  const [creditNoteData, setCreditNoteData] = useState({
    credit_note_number: '',
    original_invoice: null,
    original_invoice_number: '',
    credit_reason_notes: '',
    hms_entity: null,
    hms_entity_name: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    due_date_basis: 'INVOICE_DATE',
    partner: null,
    partner_name: '',
    invoice_address: {},
    payment_terms: null,
    internal_reference: '',
    supply_reference: '',
    supply_reference_visible_on_invoice: false,
    currency: 'USD',
    number_format: 'US',
    template: null,
    template_type: 'solids_eng',
    bank_account: null,
    cost_center: null,
    cost_category: null,
    reporting_month: '',
    notes: '',
    notes_visible_on_invoice: false,
    net_amount: 0,
    vat_amount: 0,
    gross_amount: 0
  });

  // Where this screen hands the user back to. **2026-09-09, HMS:** *"could this
  // stay in the delivery pls"*. The exits below used to hard-code '/invoicing/credit-notes';
  // an operator who arrived from their delivery was dropped into the Finance
  // invoice table instead of back on the job they were doing. One shared rule
  // now decides for all six invoice screens: you return to whichever screen you
  // actually came in from. See components/shared/useDocumentReturn.js.
  const { returnLabel, goBack, goBackAfterSave } = useDocumentReturn({
    listPath: '/invoicing/credit-notes',
    listLabel: 'Credit Notes',
    user,
    sourceDeliveryId: creditNoteData.source_delivery || null,
    sourceDeliveryNumber: creditNoteData.source_delivery_number || null,
  });

  
  // Line items
  const [lineItems, setLineItems] = useState([]);
  
  // Vessel info
  const [vesselInfo, setVesselInfo] = useState({
    vessel_name: '',
    imo_number: '',
    delivery_date: '',
    bl_number: '',
    bl_date: '',
    loading_port: '',
    discharge_port: '',
    incoterms: ''
  });
  
  // Load existing credit note (extracted so it can be called from multiple places)
  const loadCreditNote = async () => {
    if (!id) return;
    
    try {
      setIsLoading(true);
      setLoadProblem(null);
      const data = await invoicingService.fetchCreditNote(id);
      setDocumentIssuerName(data.issuer_name || null);
      
      // Populate credit note data
      setCreditNoteData({
        credit_note_number: data.credit_note_number || '',
        original_invoice: data.original_invoice,
        original_invoice_number: data.original_invoice_number || '',
        credit_reason_notes: data.credit_reason_notes || '',
        hms_entity: data.hms_entity,
        hms_entity_name: data.hms_entity_name,
        invoice_date: data.invoice_date,
        due_date: data.due_date,
        due_date_basis: data.due_date_basis || 'INVOICE_DATE',
        partner: data.partner,
        partner_name: data.partner_name,
        invoice_address: data.invoice_address || {},
        payment_terms: data.payment_terms,
        internal_reference: data.internal_reference || '',
        supply_reference: data.supply_reference || '',
        supply_reference_visible_on_invoice: data.supply_reference_visible_on_invoice || false,
        currency: data.currency,
        number_format: data.number_format || 'US',
        template: data.template,
        template_type: data.template_type,
        bank_account: data.bank_account,
        // Pledge: copied from the invoice this note corrects (read-only).
        ...pledgeOfDocument(data),
        cost_center: data.cost_center,
        cost_category: data.cost_category,
        reporting_month: data.reporting_month || '',
        notes: data.notes || '',
        notes_visible_on_invoice: data.notes_visible_on_invoice || false,
        source_delivery: data.source_delivery || null,
        source_delivery_number: data.source_delivery_number || null,
        net_amount: data.net_amount,
        vat_amount: data.vat_amount,
        gross_amount: data.gross_amount
      });
      
      // Store credit note status
      setCreditNoteStatus(data.status);
      
      // Populate line items
      if (data.line_items) {
        setLineItems(data.line_items);
      }
      
      // Populate vessel info
      if (data.vessel_info) {
        setVesselInfo({
          vessel_name: data.vessel_info.vessel_name || '',
          imo_number: data.vessel_info.imo_number || '',
          delivery_date: data.vessel_info.delivery_date || '',
          bl_number: data.vessel_info.bl_number || '',
          bl_date: data.vessel_info.bl_date || '',
          loading_port: data.vessel_info.loading_port || '',
          discharge_port: data.vessel_info.discharge_port || '',
          incoterms: data.vessel_info.incoterms || ''
        });
      }

      setLoadedId(id);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading credit note:', error);
      // Keep the answer and say it on the screen, instead of a toast and a
      // bounce to the list that never said whether it was missing or refused.
      setLoadProblem(describeLoadFailure(error));
      setIsLoading(false);
    }
  };

  // Load existing credit note if editing
  useEffect(() => {
    loadCreditNote();
  }, [id, loadAttempt]);
  
  // Load invoice data for pre-fill if invoiceId in URL params
  useEffect(() => {
    const loadInvoicePrefill = async () => {
      const urlParams = new URLSearchParams(location.search);
      const invoiceId = urlParams.get('invoiceId');
      
      // Only prefill for new credit notes (not editing existing)
      if (invoiceId && !id && !sourceInvoice) {
        try {
          setIsLoading(true);
          console.log(`Pre-filling credit note from invoice ${invoiceId}`);
          
          const prefillData = await invoicingService.createCreditNoteFromInvoice(invoiceId);
          
          // Set credit note header data
          setCreditNoteData(prev => ({
            ...prev,
            ...prefillData,
            _prefilled_from_invoice: invoiceId
          }));
          
          // Set line items
          if (prefillData.line_items) {
            setLineItems(prefillData.line_items);
          }
          
          // Set vessel info
          if (prefillData.vessel_info) {
            setVesselInfo(prefillData.vessel_info);
          }
          
          setIsLoading(false);
        } catch (error) {
          console.error('Error pre-filling from invoice:', error);
          showError('Failed to load invoice data. Please try again.');
          setIsLoading(false);
        }
      }
    };
    
    loadInvoicePrefill();
  }, [location.search, id, sourceInvoice]);
  
  const handleNext = () => {
    if (currentScreen < 3) {
      setCurrentScreen(currentScreen + 1);
    }
  };
  
  const handlePrevious = () => {
    if (currentScreen > 1) {
      setCurrentScreen(currentScreen - 1);
    }
  };
  
  // Row 125: with a credit note in the address, only a note that actually
  // loaded may be saved -- otherwise the update would write the blank editor
  // over it. Both save buttons ask this before choosing update or create.
  const refuseUnloadedSave = () => {
    if (id && loadedId !== id) {
      showError("This credit note hasn't loaded, so it can't be saved. Reload the page and try again.", 8000);
      return true;
    }
    return false;
  };

  const handleSaveDraft = async () => {
    if (refuseUnloadedSave()) return;
    try {
      setIsSaving(true);
      
      const payload = {
        ...creditNoteData,
        status: 'DRAFT',
        // issuer_employee / created_by are server-owned (read_only on every invoice
        // serializer, stamped once at create). Posting the session user here did
        // nothing today and would have re-stamped the issuer the day the field
        // became writable.
        line_items: lineItems,
        vessel_info: vesselInfo
      };
      
      let response;
      if (documentId) {
        response = await invoicingService.updateCreditNote(documentId, payload);
        showSuccess('Credit note draft saved successfully');
      } else {
        response = await invoicingService.createCreditNote(payload);
        showSuccess(`Credit note ${response.credit_note_number} created as draft`);
        // `replace`, not `push`: Back must not return to an empty create form
        // for a document that now exists. These two screens already stayed on
        // the document after a draft save -- they are the pattern the other
        // four have now been brought in line with.
        history.replace(`/invoicing/credit-notes/edit/${response.id}`);
      }
      
      setIsSaving(false);
    } catch (error) {
      console.error('Error saving draft:', error);
      showError(extractApiError(error, 'Failed to save draft. Please try again.'), 8000);
      setIsSaving(false);
    }
  };
  
  const handleSaveAndWorkflow = async () => {
    if (refuseUnloadedSave()) return;
    try {
      setIsSaving(true);
      
      // First, save as draft if new or update if existing
      const payload = {
        ...creditNoteData,
        status: 'DRAFT',
        // issuer_employee / created_by are server-owned (read_only on every invoice
        // serializer, stamped once at create). Posting the session user here did
        // nothing today and would have re-stamped the issuer the day the field
        // became writable.
        line_items: lineItems,
        vessel_info: vesselInfo
      };
      
      let savedCreditNote;
      if (documentId) {
        savedCreditNote = await invoicingService.updateCreditNote(documentId, payload);
      } else {
        savedCreditNote = await invoicingService.createCreditNote(payload);
      }
      
      // Then submit for approval using the explicit endpoint
      const approvalResponse = await invoicingService.submitCreditNoteForApproval(savedCreditNote.id);
      
      showSuccess(
        `Credit note ${isEditMode ? 'updated and ' : ''}submitted successfully! ` +
        `Status: Pending Approval. Financial approvers have been notified.`,
        8000
      );
      
      // Redirect back to credit note list after workflow submission
      setTimeout(() => {
        goBackAfterSave(savedCreditNote?.id || id);
      }, 1500);
      
      setIsSaving(false);
    } catch (error) {
      console.error('Error submitting credit note:', error);
      showError(extractApiError(error, 'Failed to submit credit note. Please try again.'), 10000);
      setIsSaving(false);
    }
  };
  
  const handleFinancialApprovalAction = async ({ action, rejection_reason, notes }) => {
    try {
      let response;
      
      if (action === 'approve') {
        response = await invoicingService.approveCreditNote(id, notes);
        console.log('Approve response:', response);
        
        const creditNoteNumber = response?.credit_note?.credit_note_number || creditNoteData.credit_note_number || 'Credit Note';
        showSuccess(`${creditNoteNumber} approved successfully!`, 8000);
      } else if (action === 'reject') {
        response = await invoicingService.rejectCreditNote(id, rejection_reason);
        console.log('Reject response:', response);
        showSuccess(`Credit note rejected. The submitter has been notified.`, 8000);
      }
      
      // Reload credit note to get updated status
      await loadCreditNote();
      
      // Redirect to credit note list after brief delay
      setTimeout(() => goBackAfterSave(id), 2000);
      
    } catch (error) {
      console.error('Error processing approval action:', error);
      console.error('Error details:', error.response?.data);
      // Shown here, as the other invoice screens do: rethrown, the approval
      // modal's technical-text filter read "GL account on line 2" as a stack
      // trace and replaced the refusal with "Failed to process approval".
      showError(extractApiError(error, 'Failed to process approval action'), 8000);
    }
  };
  
  const handleApprovalModalClose = () => {
    setShowApprovalModal(false);
  };
  
  if (isLoading) {
    return (
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="invoice-page-container credit-note-screen">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading credit note...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (id && loadProblem) {
    return (
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="invoice-page-container credit-note-screen">
          <RecordLoadProblem
            problem={loadProblem}
            recordName="credit note"
            areaName="invoicing"
            backLabel={returnLabel}
            onBack={goBack}
            onRetry={() => setLoadAttempt((n) => n + 1)}
          />
        </div>
      </Layout>
    );
  }

  const handlePreviewPdf = async () => {
    try {
      setIsLoadingPdf(true);
      const blob = await invoicingService.downloadCreditNotePDF(documentId, { preview: true });
      const url = URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
      setShowPdfPreview(true);
    } catch (error) {
      console.error('Error loading PDF preview:', error);
      showError('Failed to load PDF preview. Please try again.');
    } finally {
      setIsLoadingPdf(false);
    }
  };

  const handleClosePdfPreview = () => {
    setShowPdfPreview(false);
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl(null);
    }
  };

  // Shared pill stepper. `completed` keeps the old visited-before meaning:
  // any screen behind the current one is ticked. Free-jump is unchanged.
  const stepperSections = [
    { id: '1', label: 'Header & Partner', completed: currentScreen > 1 },
    { id: '2', label: 'Products & Vessel', completed: currentScreen > 2 },
    { id: '3', label: 'Bank & Notes', completed: currentScreen > 3 }
  ];

  // Mode reads in the shared header's title slot; the old all-caps was the
  // legacy header's own styling, the shared title carries no transform.
  const headerTitle = isReadOnly
    ? 'View Credit Note'
    : (isEditMode ? 'Edit Credit Note' : 'Create Credit Note');

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="invoice-page-container credit-note-screen">
        {/* The arrow goes back to wherever this screen was opened from
            (useDocumentReturn); backLabel is its "Back to ..." wording. */}
        <HMSPageHeader
          onBack={goBack}
          backLabel={returnLabel}
          title={headerTitle}
          kicker="Finance"
          className="invoice-header--papers"
          actions={
            <>
              {/* The delivery link rides in the actions cluster as a chip; it
                  used to sit under the old header's subtitle line. */}
              <DeliveryLinkBadge
                deliveryId={creditNoteData.source_delivery}
                deliveryNumber={creditNoteData.source_delivery_number}
              />

              {id && <PinToDeskButton entityType="credit_note" objectId={id} />}

              {/* Action buttons - only show if NOT read-only */}
              {!isReadOnly && (
                <>
                  <button
                    className="btn-secondary"
                    onClick={handleSaveDraft}
                    disabled={isSaving}
                    style={{ minWidth: '120px' }}
                  >
                    <FontAwesomeIcon icon={faSave} /> Save Draft
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleSaveAndWorkflow}
                    disabled={isSaving}
                    style={{ minWidth: '160px' }}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} /> Save & Workflow
                  </button>
                </>
              )}

              {/* IMT-only: Abacus ERP sync, offered once the record is saved. */}
              {isEditMode && id && (

                <AbacusStatus model="CreditNote" recordId={id} />

              )}

              {/* Preview PDF - any saved note at any status, VIEW mode included */}
              {isEditMode && (
                <button
                  className="btn-primary"
                  onClick={handlePreviewPdf}
                  disabled={isLoadingPdf}
                  style={{ minWidth: '160px' }}
                >
                  <FontAwesomeIcon icon={faFilePdf} /> {isLoadingPdf ? 'Loading...' : 'Preview PDF'}
                </button>
              )}

              {isEditMode && (
                <button
                  className="btn-secondary"
                  onClick={invoicePapers.toggle}
                  style={{ minWidth: '140px' }}
                  title="Show the original invoice and the delivery papers and contracts beside this credit note"
                  aria-pressed={invoicePapers.isOpen}
                  data-testid="invoice-papers-toggle"
                >
                  <FontAwesomeIcon icon={faColumns} /> Invoice Papers
                </button>
              )}
            </>
          }
        />
        
        {/* Progress Indicator */}
        {/* [ Credit Note | Invoice Papers ]: the same split as the delivery's
            Contracts & Notes. Closed, the credit note renders exactly as before. */}
        <div className={`record-split invoice-papers-split${documentId && invoicePapers.isOpen ? ' record-split--open' : ''}`}>
        <div className="record-split-content invoice-papers-record">
        <div className="hms-stepper-bar">
          <HMSStepper
            sections={stepperSections}
            activeId={String(currentScreen)}
            onStepClick={(id) => setCurrentScreen(Number(id))}
          />
        </div>
        
        {/* Financial Approver Actions - Show only for financial approvers when credit note is pending approval */}
        {isEditMode && creditNoteStatus === 'PENDING_APPROVAL' && user?.is_financial_approver && (
          <div className="financial-approver-banner">
            <div className="financial-approver-banner-content">
              <div className="financial-approver-banner-icon">
                <FontAwesomeIcon icon={faUserShield} />
              </div>
              <div className="financial-approver-banner-text">
                <h4>Financial Approval Required</h4>
                <p>This credit note is pending financial approval. As a Financial Approver, you can approve or reject this credit note.</p>
              </div>
              <button
                className="financial-approver-action-btn"
                onClick={() => setShowApprovalModal(true)}
              >
                <FontAwesomeIcon icon={faCheckCircle} />
                Approve / Reject
              </button>
            </div>
          </div>
        )}
        
        {/* Screen Content */}
        <div className="invoice-screens-container">
          {currentScreen === 1 && (
            <CreditNoteScreen1
              creditNoteData={creditNoteData}
              setCreditNoteData={setCreditNoteData}
              setLineItems={setLineItems}
              setVesselInfo={setVesselInfo}
              onNext={() => setCurrentScreen(2)}
              isReadOnly={isReadOnly}
              issuerName={documentIssuerName}
              isPersisted={Boolean(id)}
            />
          )}
          {currentScreen === 2 && (
            <CreditNoteScreen2
              creditNoteData={creditNoteData}
              setCreditNoteData={setCreditNoteData}
              lineItems={lineItems}
              setLineItems={setLineItems}
              vesselInfo={vesselInfo}
              setVesselInfo={setVesselInfo}
              onPrevious={() => setCurrentScreen(1)}
              onNext={() => setCurrentScreen(3)}
              isReadOnly={isReadOnly}
            />
          )}
          {currentScreen === 3 && (
            <CreditNoteScreen3
              creditNoteData={creditNoteData}
              setCreditNoteData={setCreditNoteData}
              onPrevious={() => setCurrentScreen(2)}
              isReadOnly={isReadOnly}
            />
          )}
        </div>
        </div>

        {documentId && (
          <DocumentsSplitPane
            isOpen={invoicePapers.isOpen}
            onClose={invoicePapers.close}
            title="Invoice Papers"
          >
            <RelatedDocumentsPane kind="credit-note" invoiceId={documentId} />
          </DocumentsSplitPane>
        )}
        </div>
      </div>
      
      {/* Approval Modal */}
      {showApprovalModal && (
        <FinancialApprovalModal
          isOpen={showApprovalModal}
          onClose={handleApprovalModalClose}
          invoice={{
            ...creditNoteData,
            invoice_number: creditNoteData.credit_note_number,
            status: creditNoteStatus,
            id: id
          }}
          isFinancialApprover={user?.is_financial_approver}
          onApprovalAction={handleFinancialApprovalAction}
          documentType="Credit Note"
        />
      )}
      
      {/* Validation Modal */}
      {showDraftValidationModal && (
        <InvoiceValidationModal
          isOpen={showDraftValidationModal}
          onClose={() => setShowDraftValidationModal(false)}
          errors={draftValidationErrors}
          onGoToScreen={(screen) => {
            setCurrentScreen(screen);
            setShowDraftValidationModal(false);
          }}
        />
      )}

        {/* PDF Preview Modal */}
        <PdfPreviewModal
          isOpen={showPdfPreview}
          pdfUrl={previewPdfUrl}
          onClose={handleClosePdfPreview}
          fileName={`CreditNote_${creditNoteData.credit_note_number || 'draft'}.pdf`}
        />
    </Layout>
  );
};

export default CreateCreditNote;
